import {
  CalendarOutlined,
  CheckOutlined,
  StopOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  EllipsisOutlined,
  RollbackOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { ContactLicense, PurchaseOrder, SDK } from "@axl-dev/school-sdk";
import { Modal, Pagination, Popover, Spin, Switch, Tooltip, theme } from "antd";
import PaymentView from "@app/pages/access/ui/components/PaymentView/PaymentView";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useLoc, useSchool, useFileProvider, useRouter, useNotifier } from "@app/shared/lib/hooks";
import { Price } from "@app/shared/ui/Price";
import { TimeLoc } from "@app/shared/ui/Loc/TimeLoc";
import { generateBtnHoverCss, parseBtnHoverProps, sanitizeCssValue as sc, BtnHoverProps } from "@app/widgets/shared/btnHoverCss";
import { deviceVarCss } from "@app/widgets/shared/deviceCss";

type ContentType = "licenses" | "subscriptions" | "orders";
type SourceMode = "all" | "activeOnly" | "expiredOnly";
type CardVariant = "vertical" | "horizontal" | "horizontalReversed";

export type AccessListWidgetProps = BtnHoverProps & {
  sdk: SDK;
  contentType: ContentType;
  sourceMode: SourceMode;
  productIds: string[];
  courseIds: string[];
  libraryIds: string[];
  layout: "grid" | "list";
  cardVariant: CardVariant;
  showImage: boolean;
  cardGap: string;
  cardWidth: string;
  contentPadding: string;
  contentPaddingTablet: string;
  contentPaddingMobile: string;
  cardBorderRadius: string;
  cardBgColor: string;
  cardBorderColor: string;
  itemTitlePosition: "onImage" | "belowImage";
  itemTitleColor: string;
  itemTitleFontFamily: string;
  itemTitleFontWeight: string;
  itemTitleMarginTop: string;
  itemTitleMarginRight: string;
  itemTitleMarginBottom: string;
  itemTitleMarginLeft: string;
  imageWidth: string;
  imageHeight: string;
  titleFontSize: string;
  titleFontFamily: string;
  titleFontWeight: string;
  titleTextColor: string;
  titleTextAlign: string;
  showStatus: boolean;
  showDate: boolean;
  showPrice: boolean;
  showType: boolean;
  showPeriod: boolean;
  showExpiration: boolean;
  showDaysLeft: boolean;
  showItemTitle: boolean;
  showSubscriptionControls: boolean;
  showPaymentMethod: boolean;
  btnBgColor: string;
  btnTextColor: string;
  payBtnBgColor: string;
  payBtnTextColor: string;
  lockedBtnBgColor: string;
  lockedBtnTextColor: string;
  btnBorderRadius: string;
  btnFontSize: string;
  btnPaddingV: string;
  btnPaddingH: string;
  btnWidth: string;
  btnText: string;
  statusActiveBg: string;
  statusActiveText: string;
  statusInactiveBg: string;
  statusInactiveText: string;
  statusWarningBg: string;
  statusWarningText: string;
  showExpiredFilter: boolean;
  emptyText: string;
  emptyTextColor: string;
  emptyTextFontSize: string;
  pageSize: number;
  title?: string;
  secondaryTextColor: string;
  prefetchedLicenses?: any[];
  prefetchedSubscriptions?: any[];
  prefetchedOrders?: any[];
};

const parseIdList = (raw: string): string[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string" && x) : [];
  } catch {
    return [];
  }
};

export const parseAccessListProps = (container: HTMLElement): Omit<AccessListWidgetProps, "sdk"> => {
  const attr = (name: string, fallback = "") => container.getAttribute(name) || fallback;

  return {
    contentType: attr("data-content-type", "licenses") as ContentType,
    sourceMode: attr("data-source-mode", "all") as SourceMode,
    productIds: parseIdList(attr("data-product-ids", "")),
    courseIds: parseIdList(attr("data-course-ids", "")),
    libraryIds: parseIdList(attr("data-library-ids", "")),
    layout: attr("data-layout", "grid") as "grid" | "list",
    cardVariant: attr("data-card-variant", "vertical") as CardVariant,
    showImage: attr("data-show-image", "true") === "true",
    cardGap: attr("data-card-gap", "20px"),
    cardWidth: attr("data-card-width", "300px"),
    contentPadding: attr("data-content-padding", "10px"),
    contentPaddingTablet: attr("data-content-padding-tablet", ""),
    contentPaddingMobile: attr("data-content-padding-mobile", ""),
    cardBorderRadius: attr("data-card-border-radius", "5px"),
    cardBgColor: attr("data-card-bg-color", "#ffffff"),
    cardBorderColor: attr("data-card-border-color", "#f0f0f0"),
    itemTitlePosition: attr("data-item-title-position", "belowImage") as "onImage" | "belowImage",
    itemTitleColor: attr("data-item-title-color"),
    itemTitleFontFamily: attr("data-item-title-font-family", ""),
    itemTitleFontWeight: attr("data-item-title-font-weight", ""),
    itemTitleMarginTop: attr("data-item-title-margin-top", "0px"),
    itemTitleMarginRight: attr("data-item-title-margin-right", "0px"),
    itemTitleMarginBottom: attr("data-item-title-margin-bottom", "8px"),
    itemTitleMarginLeft: attr("data-item-title-margin-left", "0px"),
    imageWidth: attr("data-image-width", "40%"),
    imageHeight: attr("data-image-height", "160px"),
    titleFontSize: attr("data-title-font-size", "20px"),
    titleFontFamily: attr("data-title-font-family"),
    titleFontWeight: attr("data-title-font-weight", "600"),
    titleTextColor: attr("data-title-text-color"),
    titleTextAlign: attr("data-title-text-align", "left"),
    showStatus: attr("data-show-status", "true") === "true",
    showDate: attr("data-show-date", "true") === "true",
    showPrice: attr("data-show-price", "true") === "true",
    showType: attr("data-show-type", "true") === "true",
    showPeriod: attr("data-show-period", "true") === "true",
    showExpiration: attr("data-show-expiration", "true") === "true",
    showDaysLeft: attr("data-show-days-left", "true") === "true",
    showItemTitle: attr("data-show-item-title", "true") === "true",
    showSubscriptionControls: attr("data-show-subscription-controls", "true") === "true",
    showPaymentMethod: attr("data-show-payment-method", "true") === "true",
    btnBgColor: attr("data-btn-bg-color", "#52c41a"),
    btnTextColor: attr("data-btn-text-color", "#ffffff"),
    payBtnBgColor: attr("data-pay-btn-bg-color", "#1890ff"),
    payBtnTextColor: attr("data-pay-btn-text-color", "#ffffff"),
    lockedBtnBgColor: attr("data-locked-btn-bg-color", "#f5f5f5"),
    lockedBtnTextColor: attr("data-locked-btn-text-color", "#bfbfbf"),
    btnBorderRadius: attr("data-btn-border-radius", "5px"),
    btnFontSize: attr("data-btn-font-size", "14px"),
    btnPaddingV: attr("data-btn-padding-v", "4px"),
    btnPaddingH: attr("data-btn-padding-h", "15px"),
    btnWidth: attr("data-btn-width", "100%"),
    btnText: attr("data-btn-text"),
    statusActiveBg: attr("data-status-active-bg", "#52c41a"),
    statusActiveText: attr("data-status-active-text", "#ffffff"),
    statusInactiveBg: attr("data-status-inactive-bg", "#ff4d4f"),
    statusInactiveText: attr("data-status-inactive-text", "#ffffff"),
    statusWarningBg: attr("data-status-warning-bg", "#faad14"),
    statusWarningText: attr("data-status-warning-text", "#ffffff"),
    showExpiredFilter: attr("data-show-expired-filter", "true") === "true",
    emptyText: attr("data-empty-text"),
    emptyTextColor: attr("data-empty-text-color", "#aaaaaa"),
    emptyTextFontSize: attr("data-empty-text-font-size", "14px"),
    pageSize: parseInt(attr("data-page-size", "12")) || 12,
    title: attr("data-title") || undefined,
    secondaryTextColor: attr("data-secondary-text-color", "#999999"),
    ...parseBtnHoverProps(container),
  };
};



type SubscriptionCardInfo = {
  brand: string;
  number: string;
};

const getSubscriptionCardBrand = (firstSix: string | null): string => {
  const bin = firstSix ?? "";
  if (/^4/.test(bin)) return "Visa";
  if (/^220[0-4]/.test(bin)) return "МИР";
  const firstFour = Number(bin.substring(0, 4));
  if (/^5[1-5]/.test(bin) || (firstFour >= 2221 && firstFour <= 2720)) return "Mastercard";
  if (/^3[47]/.test(bin)) return "American Express";
  if (/^62/.test(bin)) return "UnionPay";
  return "";
};

export const AccessListWidget: React.FC<AccessListWidgetProps> = (props) => {
  const {
    sdk, contentType, sourceMode, productIds, courseIds, libraryIds, layout, cardVariant,
    showImage, cardGap, cardWidth, contentPadding, cardBorderRadius, cardBgColor, cardBorderColor,
    itemTitlePosition, itemTitleColor, itemTitleFontFamily, itemTitleFontWeight,
    itemTitleMarginTop, itemTitleMarginRight, itemTitleMarginBottom, itemTitleMarginLeft,
    imageWidth, imageHeight,
    showStatus, showDate, showPrice, showType, showPeriod, showExpiration, showDaysLeft, showItemTitle, showSubscriptionControls, showPaymentMethod,
    btnBgColor, btnTextColor, payBtnBgColor, payBtnTextColor, lockedBtnBgColor, lockedBtnTextColor,
    btnWidth, btnText,
    statusActiveBg, statusActiveText, statusInactiveBg, statusInactiveText,
    statusWarningBg, statusWarningText,
    showExpiredFilter,
    emptyText, emptyTextColor, emptyTextFontSize,
    pageSize, title, secondaryTextColor,
    prefetchedLicenses, prefetchedSubscriptions, prefetchedOrders,
  } = props;

  const loc = useLoc();
  const school = useSchool();
  const fileProvider = useFileProvider();
  const { history } = useRouter();
  const [notify] = useNotifier();
  const { token } = theme.useToken();
  const fmtDate = useCallback(
    (d: any, fmt: string): string => (d ? moment(d).locale(school?.lang || "ru").format(fmt) : ""),
    [school],
  );

  const openItem = useCallback(async (item: ContactLicense) => {
    if (item.type === "library" && item.library?.id) {
      history.push(`/library/${item.library.id}`);
      return;
    }
    const course = item.course;
    if (!course?.id) return;
    const result = await sdk.account.license.startCourse(course.id);
    if (!result.success) { notify(result); return; }
    history.push(`/learn/${result.body.currentCourseItemId}`);
  }, [history, notify, sdk]);

  const [licenses, setLicenses] = useState<ContactLicense[]>([]);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [subscriptionCards, setSubscriptionCards] = useState<Record<string, SubscriptionCardInfo>>({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const [payingLicenseId, setPayingLicenseId] = useState<string | null>(null);
  const [continuingId, setContinuingId] = useState<string | null>(null);
  const [stoppingId, setStoppingId] = useState<string | null>(null);
  const [showExpired, setShowExpired] = useState(sourceMode === "expiredOnly");

  const getImageUrl = useCallback((item: ContactLicense): string | undefined => {
    if (contentType === "subscriptions") {
      const key = item.product?.image?.cloudKey;
      return key ? fileProvider.getUrlByKey(key) : undefined;
    }
    const img = item.type === "course" ? item.course?.primaryImage : item.library?.primaryImage;
    if (!img) return undefined;
    const key = (img as any).cloudKey || (img as any).file?.cloudKey;
    return key ? fileProvider.getUrlByKey(key) : undefined;
  }, [contentType, fileProvider]);

  const getTitle = useCallback((item: ContactLicense): string => {
    if (contentType === "subscriptions") return item.product?.name || loc.word("Untitled", { default: "Untitled" });
    return (item.type === "course" ? item.course?.name : item.library?.name) || loc.word("Untitled", { default: "Untitled" });
  }, [contentType, loc]);


  useEffect(() => {
    if (contentType === "subscriptions" && prefetchedSubscriptions) {
      setLicenses(prefetchedSubscriptions as ContactLicense[]);
      setLoading(false);
      return;
    }
    if (contentType === "licenses" && prefetchedLicenses) {
      setLicenses(prefetchedLicenses as ContactLicense[]);
      setLoading(false);
      return;
    }
    if (contentType === "orders" && prefetchedOrders) {
      setOrders(prefetchedOrders as PurchaseOrder[]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const load = async () => {
      try {
        if (contentType === "subscriptions") {
          const result = await sdk.account.subscription.fetch();
          if (cancelled) return;
          if (result.success && result.body?.items) {
            setLicenses(result.body.items as ContactLicense[]);
          }
        } else if (contentType === "licenses") {
          const result = await sdk.account.license.fetch();
          if (cancelled) return;
          if (result.success && result.body?.items) {
            setLicenses(result.body.items as ContactLicense[]);
          }
        } else {
          const fields = "{ id, number, createdDate, totalAmount, paidAmount, currency, status, fullyPaid, completed, contents { id, quantity, product { id, name, image { id, cloudKey } } } }";
          const result = await (sdk.api.get as any)("/student/order", fields);
          if (cancelled) return;
          if (result.success && result.body?.items) {
            setOrders(result.body.items);
          }
        }
      } catch (e) {
        console.error("[AccessListWidget] fetch error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [sdk, contentType, prefetchedLicenses, prefetchedSubscriptions, prefetchedOrders]);


  const isLicenseMode = contentType === "licenses" || contentType === "subscriptions";

  const effectiveMode: SourceMode = sourceMode === "all" && showExpired ? "expiredOnly" : sourceMode;

  const filteredLicenses = useMemo(() => {
    if (!isLicenseMode) return [];
    let result = licenses;
    if (effectiveMode === "activeOnly") result = result.filter(l => l.isActive);
    else if (effectiveMode === "expiredOnly") result = result.filter(l => !l.isActive);
    if (contentType === "subscriptions" && productIds.length > 0) {
      result = result.filter(l => l.product?.id && productIds.includes(l.product.id));
    } else if (contentType === "licenses" && (courseIds.length > 0 || libraryIds.length > 0)) {
      result = result.filter(l =>
        (l.type === "course" && l.course?.id && courseIds.includes(l.course.id)) ||
        (l.type === "library" && l.library?.id && libraryIds.includes(l.library.id)));
    }
    return result;
  }, [licenses, effectiveMode, isLicenseMode, contentType, productIds, courseIds, libraryIds]);

  const filteredOrders = useMemo(() => {
    if (isLicenseMode) return [];
    let result = orders;
    if (effectiveMode === "activeOnly") result = result.filter(o => o.fullyPaid || o.completed);
    else if (effectiveMode === "expiredOnly") result = result.filter(o => !o.fullyPaid && !o.completed);
    if (productIds.length > 0) {
      result = result.filter(o => o.contents?.some((c: any) => c.product?.id && productIds.includes(c.product.id)));
    }
    return result;
  }, [orders, effectiveMode, isLicenseMode, productIds]);

  const totalItems = isLicenseMode ? filteredLicenses.length : filteredOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);

  const paginatedLicenses = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLicenses.slice(start, start + pageSize);
  }, [filteredLicenses, currentPage, pageSize]);

  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredOrders.slice(start, start + pageSize);
  }, [filteredOrders, currentPage, pageSize]);


  const isGrid = layout === "grid";
  const isH = cardVariant === "horizontal" || cardVariant === "horizontalReversed";
  const showImg = showImage;
  const titleBelow = itemTitlePosition === "belowImage" || !showImg;
  const showTitle = showItemTitle;
  const emptyLabel = emptyText || loc.word("AccessList.empty", { default: "No items" });
  const secColor = secondaryTextColor || token.colorTextSecondary;

  const badgeStyle = (bg: string, color: string): React.CSSProperties => ({
    display: "inline-flex", alignItems: "center", gap: 4,
    padding: "2px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: 500,
    backgroundColor: bg, color,
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  });

  const infoRowStyle: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 5,
    color: secColor, fontSize: "13px", marginBottom: 8,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  };

  const labelStyle: React.CSSProperties = {
    fontWeight: 600, color: secColor, fontSize: "13px",
  };

  const valueStyle: React.CSSProperties = {
    fontWeight: 500, fontSize: "13px",
  };

  const btnStyle: React.CSSProperties = {
    display: "block", width: btnWidth || "100%", maxWidth: "100%", textAlign: "center", textDecoration: "none",
    border: "none",
    backgroundColor: btnBgColor || token.colorSuccess,
    color: btnTextColor || token.colorTextLightSolid,
    cursor: "pointer",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    boxSizing: "border-box",
  };

  const payBtnStyle: React.CSSProperties = {
    ...btnStyle,
    backgroundColor: payBtnBgColor || token.colorPrimary,
    color: payBtnTextColor || token.colorTextLightSolid,
  };

  const disabledBtnStyle: React.CSSProperties = {
    ...btnStyle,
    backgroundColor: lockedBtnBgColor || token.colorFillSecondary,
    color: lockedBtnTextColor || token.colorTextTertiary,
    cursor: "not-allowed",
    pointerEvents: "none",
  };


  const renderLicenseActivityBadge = (item: ContactLicense) => {
    if (item.isSuspended && item.currentSuspension) {
      return (
        <Tooltip title={`${loc.word("ContactLicense.suspended", { default: "Suspended" })} ${loc.word("ContactLicense.suspendedUntil", { default: "until" })} ${fmtDate(item.currentSuspension.endDate, "L")}`}>
          <div style={badgeStyle(token.colorPrimary, token.colorTextLightSolid)}>
            <ClockCircleOutlined />
            <span>{loc.word("ContactLicense.suspended", { default: "Suspended" })}</span>
          </div>
        </Tooltip>
      );
    }
    if (item.isActive) {
      return (
        <div style={badgeStyle(statusActiveBg || token.colorSuccess, statusActiveText || token.colorTextLightSolid)}>
          <CheckOutlined />
          <span>{loc.word("ContactLicense.activeLabel", { default: "Active" })}</span>
        </div>
      );
    }
    if (item.isBeginInFuture) {
      return (
        <div style={badgeStyle(statusWarningBg || token.colorWarning, statusWarningText || token.colorTextLightSolid)}>
          <ClockCircleOutlined />
          <span>{loc.word("ContactLicense.startsSoon", { default: "Starts soon" })}</span>
        </div>
      );
    }
    return (
      <div style={badgeStyle(statusInactiveBg || token.colorError, statusInactiveText || token.colorTextLightSolid)}>
        <CloseOutlined />
        <span>{loc.word("ContactLicense.notActiveLabel", { default: "Not active" })}</span>
      </div>
    );
  };

  const renderSubscriptionActivityBadge = (item: ContactLicense) => {
    if (item.isSuspended && item.currentSuspension) {
      return (
        <Tooltip title={`${loc.word("ContactLicense.suspended", { default: "Suspended" })} ${loc.word("ContactLicense.suspendedUntil", { default: "until" })} ${fmtDate(item.currentSuspension.endDate, "L")}`}>
          <div style={badgeStyle(token.colorPrimary, token.colorTextLightSolid)}>
            <ClockCircleOutlined />
            <span>{loc.word("ContactLicense.suspended", { default: "Suspended" })}</span>
          </div>
        </Tooltip>
      );
    }
    if (item.isSubscriptionActive) {
      if (item.isSubscriptionEnding) {
        return (
          <div style={badgeStyle(statusWarningBg || token.colorWarning, statusWarningText || token.colorTextLightSolid)}>
            <RollbackOutlined />
            <span>{loc.word("Subscription.endingLabel", { default: "Ending" })}</span>
          </div>
        );
      }
      return (
        <div style={badgeStyle(statusActiveBg || token.colorSuccess, statusActiveText || token.colorTextLightSolid)}>
          <CheckOutlined />
          <span>{loc.word("Subscription.activeLabel", { default: "Active" })}</span>
        </div>
      );
    }
    return (
      <div style={badgeStyle(statusInactiveBg || token.colorError, statusInactiveText || token.colorTextLightSolid)}>
        <CloseOutlined />
        <span>{loc.word("Subscription.notActiveLabel", { default: "Not active" })}</span>
      </div>
    );
  };

  const renderOrderActivityBadge = (item: PurchaseOrder) => {
    if (item.fullyPaid || item.completed) {
      return (
        <div style={badgeStyle(statusActiveBg || token.colorSuccess, statusActiveText || token.colorTextLightSolid)}>
          <CheckOutlined />
          <span>{loc.word("PurchaseOrder.paidLabel", { default: "Paid" })}</span>
        </div>
      );
    }
    if (item.isPartiallyPaid) {
      return (
        <div style={badgeStyle(statusWarningBg || token.colorWarning, statusWarningText || token.colorTextLightSolid)}>
          <WarningOutlined />
          <span>{loc.word("PurchaseOrder.partiallyPaidLabel", { default: "Partially paid" })}</span>
        </div>
      );
    }
    return (
      <div style={badgeStyle(statusInactiveBg || token.colorError, statusInactiveText || token.colorTextLightSolid)}>
        <CloseOutlined />
        <span>{loc.word("PurchaseOrder.notPaidLabel", { default: "Not paid" })}</span>
      </div>
    );
  };


  const renderImageBlock = (imageUrl: string | undefined, titleText: string, statusBadge: React.ReactNode) => (
    <div style={{
      position: "relative",
      borderRadius: isH ? undefined : `${cardBorderRadius || "5px"} ${cardBorderRadius || "5px"} 0 0`,
      overflow: "hidden",
      ...(isH
        ? { width: imageWidth || "40%", flexShrink: 0, minHeight: "100px" }
        : { width: "100%", height: imageHeight || "160px" }),
      background: imageUrl ? undefined : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    }}>
      {imageUrl && (
        <img src={imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      )}
      {showStatus && (
        <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 5 }}>
          {statusBadge}
        </div>
      )}
      {showTitle && !titleBelow && (
        <div className="al-title ez-cab-title ez-access-list-title" style={{
          position: "absolute", bottom: 0, width: "100%",
          padding: "10px",
          marginTop: itemTitleMarginTop || undefined,
          marginRight: itemTitleMarginRight || undefined,
          marginBottom: itemTitleMarginBottom || undefined,
          marginLeft: itemTitleMarginLeft || undefined,
          color: itemTitleColor || token.colorTextLightSolid, textShadow: "1px 1px 1px #000",
          background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.7) 100%)",
          fontFamily: itemTitleFontFamily || undefined,
          fontWeight: itemTitleFontWeight || 500,
        }}>{titleText}</div>
      )}
    </div>
  );


  const renderLicenseCard = (item: ContactLicense, idx = 0) => {
    const titleText = getTitle(item);
    const imageUrl = getImageUrl(item);
    const badge = renderLicenseActivityBadge(item);
    const freeAccess = item.type === "course" ? (item.course as any)?.freeAccess : (item.library as any)?.freeAccess;
    const btnEnabled = item.isActive && (!item.isSuspended || !!freeAccess);

    return (
      <div key={`${item.id}-${idx}`} className="ez-cab-card ez-access-list-card" style={{
        width: isGrid ? (cardWidth || "300px") : "100%",
        maxWidth: "100%",
        overflow: "hidden",
        ...(isH ? { display: "flex", flexDirection: cardVariant === "horizontalReversed" ? "row-reverse" : "row" } : {}),
      }}>
        {showImg && renderImageBlock(imageUrl, titleText, badge)}

        <div style={{ padding: "var(--al-pad, 10px)", flex: isH ? 1 : undefined, minWidth: isH ? 0 : undefined, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {showTitle && titleBelow && (
            <div className="al-title ez-cab-title ez-access-list-title" style={{
              color: itemTitleColor || token.colorText,
              fontFamily: itemTitleFontFamily || undefined,
              fontWeight: itemTitleFontWeight || 500,
              marginTop: itemTitleMarginTop || "0px",
              marginRight: itemTitleMarginRight || "0px",
              marginBottom: itemTitleMarginBottom || "8px",
              marginLeft: itemTitleMarginLeft || "0px",
            }}>{titleText}</div>
          )}

          {!showImg && showStatus && (
            <div style={{ marginBottom: 8 }}>{badge}</div>
          )}

          {showType && (
            <div style={{ ...infoRowStyle, fontSize: "12px" }}>
              <span>{loc.word(`ContactLicense.type.${item.type}`)}</span>
              {item.coursePlan && (
                <>
                  <span style={{ margin: "0 4px" }}>&middot;</span>
                  <span>{loc.word("Plan")} {item.coursePlan.name || loc.word("Untitled", { default: "Untitled" })}</span>
                </>
              )}
            </div>
          )}

          {showPeriod && (
            <div style={infoRowStyle}>
              <CalendarOutlined />
              <span>{fmtDate(item.beginDate, "L")}</span>
              {item.endDate && (
                <>
                  <span>-</span>
                  <span>{fmtDate(item.endDate, "L")}</span>
                </>
              )}
            </div>
          )}

          {showExpiration && (
            <div style={infoRowStyle}>
              <ClockCircleOutlined />
              {item.isBeginInFuture ? (
                <span>
                  {loc.word("ContactLicense.starts", { default: "Starts" })}{" "}
                  {fmtDate(item.beginDate, "L LT")}
                </span>
              ) : item.daysAndHoursLeft.days === Infinity ? (
                <span>{loc.word("ContactLicense.endlessLabel", { default: "Endless" })}</span>
              ) : item.daysAndHoursLeft.hours === 0 ? (
                <span>
                  {loc.word("ContactLicense.expiredLabel", { default: "Expired" })}{" "}
                  {fmtDate(item.endDate, "L LT")}
                </span>
              ) : (
                <span>
                  {loc.word("ContactLicense.expiresInLabel", { default: "Expires" })}{" "}
                  {fmtDate(item.endDate, "L LT")}
                </span>
              )}
            </div>
          )}

          <div style={{ marginTop: "auto" }}>
            {btnEnabled ? (
              <div className="al-btn ez-cab-btn ez-access-list-btn" style={{ ...btnStyle, cursor: "pointer" }} onClick={() => openItem(item)}>
                {btnText || loc.word("AccessList.openBtn", { default: "Open" })}
              </div>
            ) : (
              <div className="al-btn ez-cab-btn ez-access-list-btn" style={disabledBtnStyle}>
                🔒 {btnText || loc.word("AccessList.openBtn", { default: "Open" })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  const renderSubscriptionCard = (item: ContactLicense, idx = 0) => {
    const titleText = getTitle(item);
    const imageUrl = getImageUrl(item);
    const badge = renderSubscriptionActivityBadge(item);
    const doStop = async () => {
      if (stoppingId) return;
      setStoppingId(item.id);
      try {
        const result = await sdk.account.subscription.save(item, { state: "paused" } as any);
        if (result.success) (item as any).state = "paused";
      } catch (e) { console.error("[AccessListWidget] stop error:", e); }
      finally { setStoppingId(null); }
    };
    const doContinue = async () => {
      if (continuingId) return;
      setContinuingId(item.id);
      try {
        const result = await sdk.account.subscription.save(item, { state: "active" } as any);
        if (result.success) (item as any).state = "active";
      } catch (e) { console.error("[AccessListWidget] continue error:", e); }
      finally { setContinuingId(null); }
    };

    return (
      <div key={`${item.id}-${idx}`} className="ez-cab-card ez-access-list-card" style={{
        width: isGrid ? (cardWidth || "300px") : "100%",
        maxWidth: "100%",
        position: "relative",
        overflow: "hidden",
        ...(isH ? { display: "flex", flexDirection: cardVariant === "horizontalReversed" ? "row-reverse" : "row" } : {}),
      }}>
        {showSubscriptionControls && (item.canPauseSubscription || item.canContinueSubscription) && (
          <div style={{ position: "absolute", top: 8, right: 8, zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
            <Popover
              placement="bottomRight"
              trigger="click"
              content={item.state === "paused" ? (
                <div
                  style={{ cursor: continuingId === item.id ? "wait" : "pointer", color: token.colorPrimary, border: `1px solid ${token.colorPrimary}`, borderRadius: 6, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                  onClick={doContinue}
                >
                  <CheckOutlined />{loc.word("Subscription.continueMsg", { default: "Continue renewing" })}
                </div>
              ) : (
                <div
                  style={{ cursor: stoppingId === item.id ? "wait" : "pointer", color: token.colorError, border: `1px solid ${token.colorError}`, borderRadius: 6, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
                  onClick={doStop}
                >
                  <StopOutlined />{loc.word("Subscription.stopMsg", { default: "Stop renewing" })}
                </div>
              )}
            >
              <span style={{ display: "inline-flex", width: 30, height: 30, borderRadius: "50%", background: "rgba(255, 255, 255, 0.85)", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>
                <EllipsisOutlined style={{ fontSize: 20, cursor: "pointer", color: "rgba(0, 0, 0, 0.65)" }} />
              </span>
            </Popover>
          </div>
        )}
        {showImg && renderImageBlock(imageUrl, titleText, badge)}

        <div style={{ padding: "var(--al-pad, 10px)", flex: isH ? 1 : undefined, minWidth: isH ? 0 : undefined, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {showTitle && titleBelow && (
            <div className="al-title ez-cab-title ez-access-list-title" style={{
              color: itemTitleColor || token.colorText,
              fontFamily: itemTitleFontFamily || undefined,
              fontWeight: itemTitleFontWeight || 500,
              marginTop: itemTitleMarginTop || "0px",
              marginRight: itemTitleMarginRight || "0px",
              marginBottom: itemTitleMarginBottom || "8px",
              marginLeft: itemTitleMarginLeft || "0px",
            }}>{titleText}</div>
          )}

          {!showImg && showStatus && (
            <div style={{ marginBottom: 8 }}>{badge}</div>
          )}

          {showDate && (
            <div style={{ marginBottom: 8 }}>
              <div style={labelStyle}>{loc.word("Subscription.beginDateLabel", { default: "Begin date" })}</div>
              <div style={valueStyle}>{fmtDate(item.beginDate, "L")}</div>
            </div>
          )}

          {showPrice && (
            <div style={{ marginBottom: 8 }}>
              <div style={labelStyle}>{loc.word("Subscription.priceLabel", { default: "Price" })}</div>
              <div style={valueStyle}>
                {item.state === "paused"
                  ? loc.word("Subscription.renawalPausedLabel", { default: "Renewal paused" })
                  : <Price price={item.product?.subsPrice ?? 0} />}
              </div>
            </div>
          )}

          {/* Charged card */}
          {showPaymentMethod && subscriptionCards[item.id] && (
            <div style={{ marginBottom: 8 }}>
              <div style={labelStyle}>{loc.word("Subscription.paymentMethodLabel", { default: "Charged from" })}</div>
              <div style={valueStyle}>
                {subscriptionCards[item.id].brand
                  ? `${subscriptionCards[item.id].brand} · ${subscriptionCards[item.id].number}`
                  : subscriptionCards[item.id].number}
              </div>
            </div>
          )}

          {/* Payment date / Valid until */}
          {showExpiration && (
            <div style={{ marginBottom: 8 }}>
              {item.state === "paused" && item.isSubscriptionActive ? (
                <>
                  <div style={labelStyle}>{loc.word("Subscription.validUntilLabel", { default: "Valid until" })}</div>
                  <div style={valueStyle}>{fmtDate(item.endDate, "L")}</div>
                </>
              ) : (
                <>
                  <div style={labelStyle}>{loc.word("Subscription.validUntilLabel", { default: "Valid until" })}</div>
                  <div style={valueStyle}>
                    {item.isSubscriptionEnded
                      ? "-"
                      : item.endDate
                        ? fmtDate(item.endDate, "L")
                        : loc.word("Subscription.endlessLabel", { default: "Endless" })}
                  </div>
                </>
              )}
            </div>
          )}

          {showDaysLeft && item.isSubscriptionActive && !item.isSubscriptionEnded && item.daysAndHoursLeft
            && item.daysAndHoursLeft.days !== Infinity && item.daysAndHoursLeft.hours !== 0 && (
            <div style={{ ...labelStyle, marginBottom: 8, display: "flex", gap: 4, alignItems: "baseline", flexWrap: "wrap" }}>
              <span className="ez-access-list-days-left-label">{loc.word("Subscription.daysLeftLabel", { default: "Left" })}</span>
              <TimeLoc
                className="ez-access-list-days-left"
                value={item.daysAndHoursLeft.days}
                measure="days"
                renderValue={(v) => <span className="ez-access-list-days-left-value" data-days={v}>{v}</span>}
              />
            </div>
          )}

          <div style={{ marginTop: "auto" }}>
            <div
              className="al-btn ez-cab-btn ez-access-list-btn"
              style={{ ...(item.isSubscriptionEnded ? btnStyle : payBtnStyle), cursor: "pointer" }}
              onClick={() => setPayingLicenseId(item.id)}
            >
              {btnText || (item.isSubscriptionEnded
                ? loc.word("Subscription.resumeMsg", { default: "Resume" })
                : loc.word("Subscription.renewMsg", { default: "Renew" }))}
            </div>
          </div>
        </div>
      </div>
    );
  };


  const renderOrderCard = (item: PurchaseOrder, idx = 0) => {
    const titleText = `${loc.word("PurchaseOrder", { default: "Order" })} #${item.number}`;
    const firstProduct = item.contents?.[0]?.product;
    const imgKey = (firstProduct as any)?.image?.cloudKey;
    const imageUrl = imgKey ? fileProvider.getUrlByKey(imgKey) : undefined;
    const badge = renderOrderActivityBadge(item);
    const isPaid = item.fullyPaid || item.completed;

    return (
      <div key={`${item.id}-${idx}`} className="ez-cab-card ez-access-list-card" style={{
        width: isGrid ? (cardWidth || "300px") : "100%",
        maxWidth: "100%",
        overflow: "hidden",
        ...(isH ? { display: "flex", flexDirection: cardVariant === "horizontalReversed" ? "row-reverse" : "row" } : {}),
      }}>
        {showImg && renderImageBlock(imageUrl, titleText, badge)}

        <div style={{ padding: "var(--al-pad, 10px)", flex: isH ? 1 : undefined, minWidth: isH ? 0 : undefined, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {showTitle && titleBelow && (
            <div className="al-title ez-cab-title ez-access-list-title" style={{
              color: itemTitleColor || token.colorText,
              fontFamily: itemTitleFontFamily || undefined,
              fontWeight: itemTitleFontWeight || 500,
              marginTop: itemTitleMarginTop || "0px",
              marginRight: itemTitleMarginRight || "0px",
              marginBottom: itemTitleMarginBottom || "8px",
              marginLeft: itemTitleMarginLeft || "0px",
            }}>{titleText}</div>
          )}

          {!showImg && showStatus && (
            <div style={{ marginBottom: 8 }}>{badge}</div>
          )}

          {showDate && (
            <div style={{ marginBottom: 8 }}>
              <div style={labelStyle}>{loc.word("Date created", { default: "Date created" })}</div>
              <div style={valueStyle}>{fmtDate(item.createdDate, "L")}</div>
            </div>
          )}

          {showPrice && (
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <div>
                <div style={labelStyle}>{loc.word("PurchaseOrder.orderAmountLabel", { default: "Order amount" })}</div>
                <div style={valueStyle}><Price price={item.totalAmount} currency={item.currency} /></div>
              </div>
              {item.isPartiallyPaid && (
                <div>
                  <div style={labelStyle}>{loc.word("PurchaseOrderPayment.paidLabel", { default: "Paid" })}</div>
                  <div style={valueStyle}><Price price={item.paidAmount} currency={item.currency} /></div>
                </div>
              )}
            </div>
          )}

          {item.contents && item.contents.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
              <Popover
                placement="bottomRight"
                content={
                  <div style={{ display: "flex", flexDirection: "column", gap: 3, maxWidth: 260 }}>
                    {item.contents.map((x: any) => (
                      <span key={x.id}>
                        {x.product?.name}&nbsp;
                        <span style={{ color: secondaryTextColor || token.colorTextSecondary, fontSize: 11 }}>&times;{x.quantity}</span>
                      </span>
                    ))}
                  </div>
                }
              >
                <EllipsisOutlined style={{ fontSize: 20, cursor: "pointer", color: secondaryTextColor || token.colorTextSecondary }} />
              </Popover>
            </div>
          )}
          <div style={{ marginTop: "auto" }}>
            {isPaid ? (
              <div className="al-btn ez-cab-btn ez-access-list-btn" style={disabledBtnStyle}>
                {btnText || loc.word("AccessList.payBtn", { default: "Pay" })}
              </div>
            ) : (
              <div
                className="al-btn ez-cab-btn ez-access-list-btn"
                style={{ ...payBtnStyle, cursor: "pointer" }}
                onClick={() => setPayingOrderId(item.id)}
              >
                {btnText || loc.word("AccessList.payBtn", { default: "Pay" })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


  const scopeId = useMemo(() => `ez-al-${Math.random().toString(36).slice(2, 8)}`, []);
  const hoverCss = useMemo(() => generateBtnHoverCss(`.${scopeId}`, ".al-btn, button, a", props), [scopeId, props.btnHoverEffect, props.btnHoverBgColor, props.btnHoverTextColor, props.btnHoverShadow, props.btnHoverScale, props.btnHoverTransitionDuration]);
  const gridCss = useMemo(() => !isGrid ? "" : `
    .${scopeId} .al-grid { display: flex; flex-wrap: wrap; align-items: stretch; gap: ${sc(cardGap || "20px")}; }
  `, [scopeId, isGrid, cardGap]);
  const deviceCss = useMemo(() =>
    deviceVarCss(`.${scopeId}`, [
      { name: "al-pad", base: contentPadding || "10px", tablet: props.contentPaddingTablet, mobile: props.contentPaddingMobile },
    ]),
    [scopeId, contentPadding, props.contentPaddingTablet, props.contentPaddingMobile]);
  const themeCss = useMemo(() =>
    `.${scopeId} .ez-access-list-card{border:1px solid ${sc(cardBorderColor || token.colorBorder)};border-radius:${sc(cardBorderRadius || "5px")};background-color:${sc(cardBgColor || token.colorBgElevated)};}`
    + `.${scopeId} .ez-access-list-btn{border-radius:${sc(props.btnBorderRadius || "5px")};font-size:${sc(props.btnFontSize || "14px")};padding:${sc(props.btnPaddingV || "4px")} ${sc(props.btnPaddingH || "15px")};}`,
    [scopeId, cardBorderColor, cardBorderRadius, cardBgColor, token.colorBorder, token.colorBgElevated, props.btnBorderRadius, props.btnFontSize, props.btnPaddingV, props.btnPaddingH]);
  useEffect(() => {
    if (contentType !== "subscriptions" || !showPaymentMethod) return;

    let cancelled = false;
    (async () => {
      const [cardsResult, linksResult] = await Promise.all([
        sdk.api.get<{ items: any[] }>("/student/payment-method"),
        (sdk.api.get as any)("/student/subscription", "{id,paymentMethodId}"),
      ]);
      if (cancelled) return;

      const cards = cardsResult.success ? (cardsResult.response?.items ?? []) : [];
      const links = linksResult?.success ? (linksResult.response?.body?.items ?? linksResult.response?.items ?? []) : [];

      const cardById: Record<string, SubscriptionCardInfo> = {};
      cards.forEach((card: any) => {
        const prefix = card.cardFirstSixDigits
          ? `${card.cardFirstSixDigits.substring(0, 4)} ${card.cardFirstSixDigits.substring(4)}•• ••••`
          : "•••• •••• ••••";
        cardById[card.id] = {
          brand: getSubscriptionCardBrand(card.cardFirstSixDigits),
          number: card.cardLastFourDigits ? `${prefix} ${card.cardLastFourDigits}` : "",
        };
      });

      const byLicense: Record<string, SubscriptionCardInfo> = {};
      links.forEach((link: any) => {
        const card = link.paymentMethodId ? cardById[link.paymentMethodId] : undefined;
        if (card) byLicense[link.id] = card;
      });

      setSubscriptionCards(byLicense);
    })();

    return () => { cancelled = true; };
  }, [sdk, contentType, showPaymentMethod]);


  if (loading) {
    return <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}><Spin /></div>;
  }

  const isEmpty = isLicenseMode ? paginatedLicenses.length === 0 : paginatedOrders.length === 0;

  return (
    <div className={scopeId}>
      <style>{hoverCss}{gridCss}{deviceCss}{themeCss}</style>
      {title && (
        <div style={{
          fontSize: props.titleFontSize || "20px",
          fontWeight: (props.titleFontWeight as any) || 600,
          fontFamily: props.titleFontFamily || undefined,
          color: props.titleTextColor || undefined,
          textAlign: (props.titleTextAlign as any) || "left",
          marginBottom: "12px",
        }}>{title}</div>
      )}

      {showExpiredFilter && sourceMode === "all" && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontWeight: 500 }}>
          <span>{loc.word("ContectLicense.showExpiredMsg", { default: "Show expired" })}</span>
          <Switch
            checked={showExpired}
            onChange={(checked) => { setShowExpired(checked); setCurrentPage(1); }}
          />
        </div>
      )}

      <div style={{ minHeight: 120 }}>
        {isEmpty ? (
          <div style={{
            textAlign: "center", padding: "40px 20px",
            color: emptyTextColor || token.colorTextTertiary, fontSize: emptyTextFontSize || "14px",
          }}>{emptyLabel}</div>
        ) : (
          <>
            <div
              className={isGrid ? "al-grid" : ""}
              style={isGrid ? {} : { display: "flex", flexDirection: "column", gap: cardGap || "20px" }}
            >
              {isLicenseMode
                ? paginatedLicenses.map((item, idx) =>
                    contentType === "subscriptions"
                      ? renderSubscriptionCard(item, idx)
                      : renderLicenseCard(item, idx)
                  )
                : paginatedOrders.map((item, idx) => renderOrderCard(item, idx))
              }
            </div>

            {totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
                <Pagination
                  current={currentPage}
                  total={totalItems}
                  pageSize={pageSize}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>

      {payingOrderId && (
        <Modal
          open={true}
          title={loc.word("PurchaseOrder.paymentLabel", { default: "Order payment" })}
          footer={null}
          width={600}
          destroyOnHidden
          onCancel={() => setPayingOrderId(null)}
        >
          <PaymentView orderId={payingOrderId} />
        </Modal>
      )}
      {payingLicenseId && (
        <Modal
          open={true}
          title={loc.word("Subscription.paymentLabel", { default: "Subscription payment" })}
          footer={null}
          width={600}
          destroyOnHidden
          onCancel={() => setPayingLicenseId(null)}
        >
          <PaymentView licenseId={payingLicenseId} />
        </Modal>
      )}
    </div>
  );
};
